import {
    Component,
    OnInit,
    Input,
    ChangeDetectionStrategy,
    Output,
    EventEmitter,
    ViewChild,
    ElementRef,
    OnDestroy
} from '@angular/core';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { PublishService } from '../../publish.service';
import { CameraService } from 'src/app/engine/camera.service';
import { ColorModel } from 'src/app/edit/models/color.model';
import { CameraModel } from 'src/app/engine/models/camera.model';
import { Observable, Subject, interval, Subscription } from 'rxjs';
import { map, takeUntil, filter, tap } from 'rxjs/operators';
import { WidgetOptions } from '../../models/widget-options.model';
import { animationFrame } from 'rxjs/internal/scheduler/animationFrame';
import { ScreenDimensions } from 'src/app/shared/models/screen.dimensions.model';
import { SerializedParams } from '../../models/serialized-param.model';
import { BackgroundModel } from 'src/app/engine/models/background.model';

declare const HumanAPI: any;

const scaleSmoothFactor = 0.95;

enum HumanAPIEvent {
  HumanReady = 'human.ready',
  CameraUpdated = 'camera.updated'
}

enum HumanAPIMessage {
  GetCameraInfo = 'camera.info',
  SetCameraOrbit = 'camera.orbit',
  SetBackgroundColor = 'ui.setBackground',
  GetBackgroundColor = 'ui.getBackground',
  SetWidgetConfig = 'ui.setDisplay',
  SetInitialEnabled = 'widget.enableInitial',
  SetInitialDisabled = 'widget.disableInitial',
  SetScrollEnabled = 'widget.enableScroll',
  SetScrollDisabled = 'widget.disableScroll',
  SetToolsDisplay = 'widget.toolsDisplay',
  GetSnapshot = 'ui.snapshot'
}

@Component({
  selector: 'app-publish-widget',
  templateUrl: './publish-widget.component.html',
  styleUrls: ['./publish-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PublishWidgetComponent implements OnInit, OnDestroy { 
  @ViewChild('widgetContainer')
  containerRef: ElementRef;

  @ViewChild('widgetFrame')
  frameRef: ElementRef;

  @Input()
  model: BookmarkModel;

  @Input()
  width: number;

  @Input()
  height: number;

  @Input()
  scalable = false;

  @Input()
  set bgColor(value: BackgroundModel) {
    this._bgColor = value;
    if (this._ready) {
      this.renderWidgetBGColor();
    }
  }

  @Input()
  set widgetParams(value: WidgetOptions) {
    this._params = value;
    this._rotateDegrees = value ? value.loadRotate : 0;
    this.setWidgetUIConfig();
  }

  @Output() 
  cameraUpdated = new EventEmitter<CameraModel>();

  private ngUnsubscribe = new Subject();

  get topOffset(): number {
    const containerHeight = this.elRef.nativeElement.offsetHeight;
    const targetOffset = (containerHeight - this.height) / 2;
    return this.scaleFactor < 1 ? targetOffset : Math.max(0, targetOffset);
  }

  get leftOffset(): number {
    const containerWidth = this.elRef.nativeElement.offsetWidth;
    const targetOffset = (containerWidth - this.width) / 2;
    return this.scaleFactor < 1 ? targetOffset : Math.max(0, targetOffset);
  }

  get scaleFactor(): number {
    if (!this.scalable) {
      return 1;
    } else {
      const { offsetWidth, offsetHeight } = this.containerRef.nativeElement;
      const xScale = offsetWidth / this.width;
      const yScale = offsetHeight / this.height;
      const targetScale = Math.min(xScale, yScale, 1);

      if (targetScale === 1) { // default scale
        return 1;
      } else { // scaled model: smooth
        return targetScale * scaleSmoothFactor;
      }
    }
  }

  private _apiRef: any;
  private _params: WidgetOptions;
  private _defaultCamera: CameraModel;
  private _currentCamera: CameraModel;

  private _defaultColor: BackgroundModel;
  private _bgColor: BackgroundModel;
  private _rotateDegrees: number;
  private _ready = false;

  constructor(
    private elRef: ElementRef,
    private publishService: PublishService,
    private cameraService: CameraService) {
  }

  ngOnInit() {
    const frameId = this.frameRef.nativeElement.id;
    this._apiRef = new HumanAPI({ iframeId: frameId });

    // listen for API ready state
    this.getAPIEvent(HumanAPIEvent.HumanReady)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {

        // ready state
        this._ready = true;

        this.setupCameraTracking();
        this.setupCameraRotation();
        this.setWidgetUIConfig();

        // setup background color
        this.getCurrentBGColor()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((color) => {
            this._defaultColor = color;
            // initial bg color
            this.renderWidgetBGColor();
          });
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  setWidgetUIConfig() {
    // API ready
    if (this._apiRef) {
      if (this._params) { // set UI
        const uiConfig = this.publishService.getWidgetConfig(this._params);
        const { button, handHint } = this._params.initial;
        const initialMode = button ? 'default' : (handHint ? 'hand-hint' : '');
        const { alert, overlay } = this._params.disableScroll;
        const scrollMode = alert ? 'alert' : (overlay ? 'overlay' : '');
        const uiToolsDisplay = this._params.uiToolsDisplay;

        this.setUIOptions(uiConfig);
        this.setInitialMode(initialMode);
        this.setScrollMode(scrollMode);
        this.setToolsDisplay(uiToolsDisplay);
      } else { // disable UI
        this.setUIOptions({ all: false });
        this.setInitialMode();
        this.setScrollMode();
        this.setToolsDisplay();
      }
    }
  }

  takeSnapshot(dimensions?: ScreenDimensions): Observable<string> {
    return this.getAPIData(HumanAPIMessage.GetSnapshot, {
      width: this.width,
      height: this.height,
      ...dimensions
    });
  }

  /**
   * Orbits the camera around the model by specified degrees.
   */
  private setCameraOrbit(degrees: number) {
    this.sendAPIData(HumanAPIMessage.SetCameraOrbit, {
      yaw: degrees
    });
  }

  /**
 * Sets UI options (i.e., zoom, fullscreen, help, etc...)
 */
  private setUIOptions(uiConfig: SerializedParams) {
    this.sendAPIData(HumanAPIMessage.SetWidgetConfig, uiConfig);
  }

  /**
   * Sets the initial mode (i.e., click to interact)
   */
  private setInitialMode(initialMode: '' | 'default' | 'hand-hint' = '') {
    if (initialMode) {
      this.sendAPIData(HumanAPIMessage.SetInitialEnabled, initialMode);
    } else { // disable initial
      this.sendAPIData(HumanAPIMessage.SetInitialDisabled);
    }
  }

  /**
   * Sets the scroll override mode (for mouse wheel)
   */
  private setScrollMode(scrollMode: '' | 'alert' |'overlay' = '') {
    if (scrollMode) {
      this.sendAPIData(HumanAPIMessage.SetScrollDisabled, scrollMode);
    } else { // disable initial
      this.sendAPIData(HumanAPIMessage.SetScrollEnabled);
    }
  }

  /**
   * Sets the toolbar grouping
   */
  private setToolsDisplay(uiToolsDisplay: '' | 'primary' = '') {
    this.sendAPIData(HumanAPIMessage.SetToolsDisplay, uiToolsDisplay);
  }

  private renderWidgetBGColor() {
    this.setWidgetBGColor(this._bgColor || this._defaultColor);
  }

  private setWidgetBGColor(background: BackgroundModel) {
    if (this._apiRef && background) {
      this.sendAPIData(HumanAPIMessage.SetBackgroundColor, background);
    }
  }

  private setupCameraTracking() {

    // get initial camera
    this.getCurrentCamera()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((currentCamera) => {

        // set fixed default camera
        this.setDefaultCamera(currentCamera);

        // begin tracking camera changes
        this.getCameraUpdates()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((updateCamera) => {
            this.setUpdateCamera(updateCamera);
          });

      });
  }

  // use API to rotate widget fixed amount in an interval
  private setupCameraRotation() {
    // setup camera rotate animation
    let timeLast = performance.now();
    interval(0, animationFrame)
      .pipe(
        filter(() => !!this._rotateDegrees), // on valid rotation values
        map(() => this._rotateDegrees * (performance.now() - timeLast) * 0.001),
        tap(() => timeLast = performance.now()),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((degrees) => {
        this.setCameraOrbit(degrees);
      });
  }

  private setDefaultCamera(camera: CameraModel) {
    this._defaultCamera = camera;
    this.setUpdateCamera(camera);
  }

  private setUpdateCamera(camera: CameraModel) {
    this._currentCamera = camera;

    // #TODO: compare cameras, smooth ???
    const camerasMatch = this.cameraService.compareCameras(
      this.cameraService.roundCamera(this._defaultCamera, 3),
      this.cameraService.roundCamera(this._currentCamera, 3)
    );

    // dispatch changes: validate delta from original
    const targetCamera = !camerasMatch ? this._currentCamera : null;
    this.cameraUpdated.emit(targetCamera);
  }

  private getAPIEvent(event: HumanAPIEvent): Observable<any> {
    return new Observable<any>((observer) => {
      this._apiRef.on(event, (data: any) => {
        observer.next(data);
      });
    });
  }

  private getAPIData(message: HumanAPIMessage, payload?: any): Observable<any> {
    return new Observable<any>((observer) => {
      this._apiRef.send(message, payload, (data: any) => {
        observer.next(data);
        observer.complete();
      });
    });
  }

  private sendAPIData(message: HumanAPIMessage, payload?: any) {
    this._apiRef.send(message, payload);
  }

  private getCurrentCamera(): Observable<CameraModel> {
    return this.getAPIData(HumanAPIMessage.GetCameraInfo)
      .pipe(map((data: any) => {
        return <CameraModel>{
          eye: data.position,
          look: data.target,
          up: data.up
        };
      }));
  }

  private getCurrentBGColor(): Observable<BackgroundModel> {
    return this.getAPIData(HumanAPIMessage.GetBackgroundColor)
      .pipe(
        map((data) => {
          if (data) {
            return new BackgroundModel(
              [...[].concat(...data.colors)], // [[r,g,b,a], [r,g,b,a]] -> [r,g,b,a,r,g,b,a]
              data.gradientType,
              data.colorStops
            );
          } else {
            return null;
          }
        })
      );
  }

  private getCameraUpdates(): Observable<CameraModel> {
    return this.getAPIEvent(HumanAPIEvent.CameraUpdated)
      .pipe(map((data) => {
        const { position, target, up } = data;
        return <CameraModel>{
          eye: position,
          look: target,
          up: up
        };
      }));
  }

}
